// Réseau Pros - Page "En construction" 
// Réseau professionnel du bâtiment + Centrale d'achat groupé

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, Users, Handshake, TrendingDown, Bell, CheckCircle2,
  ArrowRight, Sparkles, Shield, Network, MessageSquare, Target,
  BadgeCheck, Rocket, Crown, Gift, Percent, Package, Truck, UserPlus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import UniversalBanner from '../../components/layout/UniversalBanner';

export default function ReseauProsPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    // Simulation - à connecter à l'API plus tard
    setTimeout(() => {
      setSubmitted(true);
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="reseau-pros-page">
      {/* Header Unifié */}
      <UnifiedHeader />
      
      {/* Navigation Principale */}
      <MainNavigation />

      {/* Bandeau de Conversion Universel */}
      <UniversalBanner variant="reseau-pros" className="py-4" />

      {/* Hero - Coming Soon */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #334155 100%)' }}>
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full filter blur-[120px] animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full filter blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full filter blur-[150px]" />
        </div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 py-16 sm:py-24">
          {/* Badge En Construction */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-8"
          >
            <motion.span 
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full backdrop-blur-sm"
            >
              <Rocket size={18} className="text-cyan-400" />
              <span className="text-sm font-semibold text-white">Lancement imminent</span>
            </motion.span>
          </motion.div>
          
          {/* Titre Principal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Le Réseau des
              <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Pros du Bâtiment
              </span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Connecte-toi avec tes pairs, partage ton expertise, 
              et <span className="text-white font-semibold">achète groupé pour payer moins cher</span>
            </p>
          </motion.div>

          {/* Les 2 Piliers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid md:grid-cols-2 gap-6 mb-12"
          >
            {/* Pilier 1 : Réseau Pro */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity blur-xl" />
              <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-6 sm:p-8 hover:border-cyan-500/30 transition-colors">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mb-5 shadow-lg shadow-cyan-500/20">
                  <Network size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Réseau Professionnel</h3>
                <p className="text-slate-300 mb-5">
                  Un espace dédié aux professionnels du bâtiment pour échanger, 
                  se recommander et développer leur activité.
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: Users, text: 'Connectez-vous avec d\'autres pros' },
                    { icon: MessageSquare, text: 'Échangez conseils et bonnes pratiques' },
                    { icon: BadgeCheck, text: 'Profils vérifiés et certifiés' },
                    { icon: Target, text: 'Opportunités business et sous-traitance' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                        <item.icon size={16} className="text-cyan-400" />
                      </div>
                      <span className="text-sm">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Pilier 2 : Centrale d'Achat */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity blur-xl" />
              <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-6 sm:p-8 hover:border-orange-500/30 transition-colors">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-5 shadow-lg shadow-orange-500/20">
                  <Handshake size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Groupement d'Achat</h3>
                <p className="text-slate-300 mb-5">
                  Ensemble, négocions les meilleurs prix. Plus on est nombreux, 
                  plus on économise sur nos achats.
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: TrendingDown, text: 'Prix négociés jusqu\'à -30%' },
                    { icon: Package, text: 'Matériaux, outillage, EPI...' },
                    { icon: Truck, text: 'Livraison groupée avantageuse' },
                    { icon: Shield, text: 'Fournisseurs vérifiés et fiables' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                        <item.icon size={16} className="text-orange-400" />
                      </div>
                      <span className="text-sm">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Formulaire d'inscription */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-lg mx-auto"
          >
            {!submitted ? (
              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl shadow-black/20">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Bell size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Soyez parmi les premiers !
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Inscrivez-vous pour être informé dès le lancement et bénéficier d'avantages exclusifs
                  </p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Votre email professionnel"
                    className="w-full h-14 px-5 bg-gray-50 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500 transition-all"
                    required
                  />
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-xl font-semibold text-lg shadow-lg shadow-cyan-500/20"
                  >
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <UserPlus size={20} className="mr-2" />
                        Me prévenir du lancement
                      </>
                    )}
                  </Button>
                </form>
                
                <p className="text-center text-xs text-gray-500 mt-4">
                  Pas de spam, juste une notification lors du lancement
                </p>
              </div>
            ) : (
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-3xl p-8 shadow-2xl shadow-black/20 text-center"
              >
                <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                  <CheckCircle2 size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Parfait, c'est noté !
                </h3>
                <p className="text-gray-600 mb-4">
                  Nous te contacterons dès que le Réseau Pros sera disponible.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full text-green-700 text-sm font-medium">
                  <Gift size={16} />
                  Avantage "Pionnier" réservé
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Section "Pourquoi ça va changer la donne" */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full text-slate-700 text-sm font-medium mb-4">
              <Sparkles size={16} className="text-amber-500" />
              L'union fait la force
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi rejoindre le mouvement ?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Fini l'isolement. Ensemble, les pros du bâtiment peuvent accomplir bien plus.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: 'Force du collectif',
                description: 'Seul on va vite, ensemble on va loin. Rejoignez une communauté de pros qui s\'entraident.',
                color: 'cyan'
              },
              {
                icon: Percent,
                title: 'Économies réelles',
                description: 'Les achats groupés permettent de négocier des tarifs inaccessibles individuellement.',
                color: 'orange'
              },
              {
                icon: Crown,
                title: 'Visibilité accrue',
                description: 'Développe ta réputation et trouve de nouvelles opportunités grâce au réseau.',
                color: 'purple'
              },
            ].map((item, index) => {
              const Icon = item.icon;
              const colors = {
                cyan: { bg: 'bg-cyan-50', icon: 'text-cyan-600', border: 'border-cyan-100' },
                orange: { bg: 'bg-orange-50', icon: 'text-orange-600', border: 'border-orange-100' },
                purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-100' },
              };
              const c = colors[item.color];
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${c.bg} ${c.border} border rounded-2xl p-6 text-center`}
                >
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-white flex items-center justify-center shadow-sm`}>
                    <Icon size={28} className={c.icon} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Programme Fondateur */}
      <section className="py-16 sm:py-20 px-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)' }}>
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/10 rounded-full filter blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-emerald-400/10 rounded-full filter blur-[80px]" />
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Badge */}
            <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-8">
              <Rocket size={16} />
              Programme Fondateur 2025-2026
            </span>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Vous êtes artisan ?
              <span className="block text-emerald-400 mt-2">Rejoignez les fondateurs</span>
            </h2>
            
            <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Bénéficiez de <span className="text-white font-semibold">1 an offert</span> et participez à la construction de la première plateforme 100% dédiée aux pros du bâtiment
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/offre-fondateur">
                <Button className="h-14 px-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-lg shadow-lg shadow-emerald-500/20">
                  <Crown size={20} className="mr-2" />
                  Devenir Fondateur
                </Button>
              </Link>
              <Link to="/compte">
                <Button variant="outline" className="h-14 px-8 border-2 border-slate-600 text-white hover:bg-slate-800 rounded-xl font-semibold">
                  En savoir plus
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Final - Autres univers */}
      <section className="py-12 px-4 bg-slate-800">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Building2 size={40} className="mx-auto mb-3 text-cyan-400" />
            <h3 className="text-xl font-bold text-white mb-3">
              En attendant le Réseau Pros...
            </h3>
            <p className="text-slate-400 mb-5 text-sm">
              Découvrez les autres univers d'AbracadaBati
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/artisans">
                <Button variant="outline" size="sm" className="border-slate-600 text-white hover:bg-slate-700">
                  Trouver un artisan
                </Button>
              </Link>
              <Link to="/produits">
                <Button variant="outline" size="sm" className="border-slate-600 text-white hover:bg-slate-700">
                  Marketplace
                </Button>
              </Link>
              <Link to="/reseau">
                <Button variant="outline" size="sm" className="border-slate-600 text-white hover:bg-slate-700">
                  Réseau Social
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer Global */}
      

      {/* Footer Spacer for mobile */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
